import { Autocomplete, TextField, Tooltip } from "@mui/material";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { MecordColors } from "Constants/Color";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import {
    autocompleteDropdownStyles,
    filterPopoverStyles,
    tableStyles,
} from "Constants/Style";
import { useFormik } from "formik";
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Accordion,
    Col,
    Form,
    Row,
    Table,
    Modal,
    Button,
} from "react-bootstrap";
import EditIcon from "@mui/icons-material/Edit";
import TextEditor from "components/TextEditor/TextEditor";
import InfoIcon from "@mui/icons-material/Info";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import ProductHistoryModal from "Common/Components/ProductHistoryModal/ProductHistoryModal";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { createChallan, fetchChallanById, updateChallan, fetchChallanCreateData } from "slices/sales_order/challan/thunk";
import { getWarehouseByUser } from "slices/Masters/warehosue/thunk";
import { RootState } from "../../../../index";
import { clearChallanState } from "slices/sales_order/challan/reducer";
import FileUpload from "Common/Components/FileUpload.tsx/FileUpload";
import { appendToFormData } from "helpers/formDataHelper";
import ChallanSplitSummary from "./components/ChallanSplitSummary";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChallanFormValues {
    challan_date: string;
    initiated_by: string;
    transporter_type: string;
    warehouse_id?: string;
    note?: string;
    attachments?: any;
    existingDocuments?: string[];
    so_number?: any;
    // Transport-type related fields
    transporter_name?: string;
    person_number?: string;
    docket_number?: string;
    company_name?: string;
    vehicle_number?: string;
    driver_name?: string;
    driver_number?: string;
    carrier_name?: string;
    train_number?: string;
    booking_number?: string;
    origin?: string;
    flight_number?: string;
    awb_number?: string;
    transport_remarks?: string;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ChallanFormProps {
    mode?: "create" | "edit";
    initialData?: Partial<ChallanFormValues> | null;
    onSuccess?: () => void;
    onCancel?: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const ChallanForm: React.FC<ChallanFormProps> = ({
    mode = "create",
    initialData = null,
    onSuccess,
    onCancel,
}) => {
    const { id: urlId } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useDispatch<any>();
    const { challanDetail, loading: reduxLoading } = useSelector((state: RootState) => state.Challan);
    const { warehouseByUserList, warehouseByUserLoading } = useSelector(
        (state: any) => state.warehouse,
    );

    // ── Unified extraction from challanDetail (Redux) ────────────────────────
    const metadata = mode === "create" ? challanDetail?.salesOrderService : challanDetail?.challan;
    
    // In edit mode, we might have nested sales_order_service OR direct fields if snapshot was flat
    const salesOrderService = mode === "edit" ? (metadata?.sales_order_service || metadata) : metadata;

    const soNumber =
        metadata?.sales_order_number || 
        metadata?.so_number || 
        salesOrderService?.sales_order_number ||
        "-";
    const customerDetails = metadata?.customer_details || salesOrderService?.customer_details || null;
    const billFrom = metadata?.bill_from || salesOrderService?.bill_from || null;
    const billTo = metadata?.bill_to || metadata?.billing_details || salesOrderService?.bill_to || null;
    const termsAndConditions = metadata?.terms_and_conditions || salesOrderService?.terms_and_conditions || [];

    const customerGstType = String(customerDetails?.gst_type ?? "").trim().toLowerCase();
    const isCompositionGst =
        customerGstType === "compositional" ||
        customerGstType === "compositional gst" ||
        customerGstType.includes("compositional");

    // ── Static dropdown stubs – replace with real API data when available ────
    const transportTypeOptions = ["Self Pickup", "Courier", "Road", "Rail", "Air"];

    // ── Local state ──────────────────────────────────────────────────────────
    // Seed products from metadata (items array, various possible keys)
    const [products, setProducts] = useState<any[]>([]);
    const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const getItemAccessories = (item: any) =>
        Array.isArray(item?.accessories) ? item.accessories : [];
    const getServiceItemId = (item: any): string | null =>
        item?.sales_order_service_item_id || null;
    const [existingDocuments, setExistingDocuments] = useState<string[]>([]);

    const [showProductsModal, setShowProductsModal] = useState(false);
    const [modalProducts, setModalProducts] = useState<any[]>([]);
    const [modalProductLabel, setModalProductLabel] = useState("");
    const [modalProductIndex, setModalProductIndex] = useState<number | null>(null);

    const [showAdditionalDetailsModal, setShowAdditionalDetailsModal] =
        useState(false);
    const [additionalDetailsIndex, setAdditionalDetailsIndex] = useState<
        number | null
    >(null);
    const [additionalDetailsDraft, setAdditionalDetailsDraft] = useState("");

    // Product History Modal State
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedSerialNo, setSelectedSerialNo] = useState<string>("");
    const [productHistory, setProductHistory] = useState<any>(null);
    const [loadingHistory, setLoadingHistory] = useState(false);

    type AccessorySerialPick = {
        inventory_product_id: string;
        serial_no: string;
        warranty_date?: string | null;
    };

    /** accessory_id -> one pick per unit qty (null = not selected yet) */
    const [accessorySerialPicks, setAccessorySerialPicks] = useState<
        Record<string, Array<AccessorySerialPick | null>>
    >({});
    const [accessorySerialOptions, setAccessorySerialOptions] = useState<
        Record<string, AccessorySerialPick[]>
    >({});
    const [accessorySerialLoading, setAccessorySerialLoading] = useState<
        Record<string, boolean>
    >({});

    const accessorySerialKey = (accessoryId: string) => String(accessoryId);

    const buildEmptyPicks = (qty: number) =>
        Array.from({ length: Math.max(0, qty) }, () => null);

    const seedPicksFromAccessory = (acc: any): Array<AccessorySerialPick | null> => {
        const qty = Math.max(0, Number(acc?.quantity || 0));
        const ids: string[] = Array.isArray(acc?.issued_inventory_product_ids)
            ? acc.issued_inventory_product_ids.filter(Boolean).map(String)
            : [];
        const serials: string[] = Array.isArray(acc?.serial_numbers)
            ? acc.serial_numbers.map((s: any) => String(s || ""))
            : [];
        return Array.from({ length: qty }, (_, i) => {
            if (!ids[i]) return null;
            return {
                inventory_product_id: ids[i],
                serial_no: serials[i] || "",
            };
        });
    };

    const fetchAccessorySerialOptions = useCallback(
        async (
            productId: string,
            warehouseId: string,
            includeIds: string[] = [],
        ) => {
            if (!productId || !warehouseId) return [];
            const params = new URLSearchParams({
                product_id: productId,
                warehouse_id: warehouseId,
                available_only: "true",
            });
            if (includeIds.length) {
                params.set("include_ids", includeIds.join(","));
            }
            const response = await Axios.get(
                `/warehouse/grn/serial-numbers-dropdown?${params.toString()}`,
            );
            const data = response?.data?.data || response?.data || [];
            return (Array.isArray(data) ? data : [])
                .map((item: any) => ({
                    inventory_product_id: String(
                        item.inventory_product_id || "",
                    ),
                    serial_no: String(
                        item.serial_no || item.serial_number || "",
                    ),
                    warranty_date: item.warranty_date
                        ? String(item.warranty_date).slice(0, 10)
                        : null,
                }))
                .filter(
                    (item: AccessorySerialPick) =>
                        item.inventory_product_id && item.serial_no,
                );
        },
        [],
    );

    const ensureAccessorySerialOptions = useCallback(
        async (
            acc: any,
            warehouseId: string,
            includeIds: string[] = [],
        ) => {
            const productId = acc?.product_id;
            const accessoryId = acc?.accessory_id;
            if (!productId || !accessoryId || !warehouseId) return;
            const key = accessorySerialKey(accessoryId);

            setAccessorySerialLoading((prev) => ({ ...prev, [key]: true }));
            try {
                const options = await fetchAccessorySerialOptions(
                    productId,
                    warehouseId,
                    includeIds,
                );
                setAccessorySerialOptions((prev) => ({
                    ...prev,
                    [key]: options,
                }));
                // Fill missing serial_no labels for already-selected inventory ids
                setAccessorySerialPicks((prev) => {
                    const current = prev[key];
                    if (!current?.length) return prev;
                    const next = current.map((pick) => {
                        if (!pick?.inventory_product_id) return pick;
                        const match = options.find(
                            (o) =>
                                o.inventory_product_id ===
                                pick.inventory_product_id,
                        );
                        if (!match) return pick;
                        if (pick.serial_no && pick.warranty_date) return pick;
                        return {
                            ...pick,
                            serial_no: pick.serial_no || match.serial_no,
                            warranty_date:
                                pick.warranty_date || match.warranty_date || null,
                        };
                    });
                    return { ...prev, [key]: next };
                });
            } catch (error) {
                console.error("Failed to load accessory serials:", error);
                toast.error(
                    `Failed to load serial numbers for ${acc.product_name || "accessory"}`,
                );
            } finally {
                setAccessorySerialLoading((prev) => ({
                    ...prev,
                    [key]: false,
                }));
            }
        },
        [fetchAccessorySerialOptions],
    );

    // Fetch product history
    const fetchProductHistory = async (serialNo: string) => {
        if (!serialNo || serialNo === "-") {
            toast.error("Invalid serial number");
            return;
        }

        setLoadingHistory(true);
        setSelectedSerialNo(serialNo);
        setShowHistoryModal(true);

        try {
            const response = await Axios.get(
                API_ENDPOINT.INVENTORY.PRODUCT_HISTORY(serialNo),
            );
            setProductHistory(response.data?.data || response.data);
        } catch (error: any) {
            toast.error(
                error.response?.data?.message || "Failed to fetch product history",
            );
            setProductHistory(null);
        } finally {
            setLoadingHistory(false);
        }
    };

    // ── Common styles ────────────────────────────────────────────────────────
    const commonInputStyle: React.CSSProperties = {
        fontSize: "0.8rem",
        minHeight: 38,
        padding: "0.375rem 0.75rem",
    };
    const commonLabelStyle: React.CSSProperties = {
        fontSize: "0.8rem",
        marginBottom: 6,
        marginTop: 12,
    };

    const requiredLabel = (label: string) => (
        <>
            {label} <span className="text-danger">*</span>
        </>
    );

    // ── Transport details builder ────────────────────────────────────────────
    const buildTransportDetails = (values: ChallanFormValues) => {
        const details: Record<string, string> = {};
        const type = (values.transporter_type || "").toLowerCase();
        switch (type) {
            case "self pickup":
                if (values.transporter_name) details.person_name = values.transporter_name;
                if (values.person_number) details.person_number = values.person_number;
                break;
            case "courier":
                if (values.docket_number) details.docket_number = values.docket_number;
                if (values.company_name) details.company_name = values.company_name;
                break;
            case "road":
                if (values.vehicle_number) details.truck_number = values.vehicle_number;
                if (values.driver_name) details.driver_name = values.driver_name;
                if (values.driver_number) details.driver_mobile = values.driver_number;
                if (values.transporter_name)
                    details.transporter_name = values.transporter_name;
                break;
            case "rail":
                if (values.carrier_name) details.carrier_name = values.carrier_name;
                if (values.train_number) details.train_number = values.train_number;
                if (values.booking_number) details.booking_number = values.booking_number;
                if (values.origin) details.origin = values.origin;
                break;
            case "air":
                if (values.carrier_name) details.carrier_name = values.carrier_name;
                if (values.flight_number) details.flight_number = values.flight_number;
                if (values.awb_number) details.awb_number = values.awb_number;
                if (values.origin) details.origin = values.origin;
                break;
        }
        if (values.transport_remarks) details.remarks = values.transport_remarks;
        return details;
    };

    // ── Validation ───────────────────────────────────────────────────────────
    const validationSchema = Yup.object().shape({
        challan_date: Yup.string().required("Challan Date is required"),
        initiated_by: Yup.string().trim().required("Initiated By is required"),
        transporter_type: Yup.string().required("Transporter Type is required"),
    });

    // ── Formik ───────────────────────────────────────────────────────────────
    const formik = useFormik<ChallanFormValues>({
        initialValues: {
            challan_date: new Date().toISOString().split("T")[0],
            initiated_by: "",
            so_number: soNumber !== "-" ? soNumber : "",
            transporter_type: "",
            warehouse_id: "",
            note: "",
            attachments: null,
            transporter_name: "",
            person_number: "",
            docket_number: "",
            company_name: "",
            vehicle_number: "",
            driver_name: "",
            driver_number: "",
            carrier_name: "",
            train_number: "",
            booking_number: "",
            origin: "",
            flight_number: "",
            awb_number: "",
            transport_remarks: "",
            ...initialData,
        },
        validationSchema,
        validateOnBlur: true,
        validateOnChange: false,
        enableReinitialize: true,
        onSubmit: async (values, { setTouched, validateForm }) => {
            const touchedFields: any = {};
            Object.keys(values).forEach((f) => (touchedFields[f] = true));
            setTouched(touchedFields);
            const errors = await validateForm();
            if (Object.keys(errors).length > 0) {
                toast.error("Please fill all mandatory fields.");
                return;
            }

            if (selectedProductIds.length === 0) {
                toast.warning("Please select at least one item.");
                return;
            }

            const selectedProducts = products.filter((p) => {
                const pid = getServiceItemId(p);
                return pid !== null && selectedProductIds.includes(pid);
            });
            const selectedHasAccessories = selectedProducts.some(
                (item) => getItemAccessories(item).length > 0,
            );
            const existingWarehouseId = challanDetail?.challan?.warehouse_id || "";
            if (selectedHasAccessories && !values.warehouse_id && !existingWarehouseId) {
                toast.error("Please select a warehouse for accessories.");
                return;
            }

            // Validate accessory serial selections (1 per unit, no duplicates)
            const usedInventoryIds = new Set<string>();
            for (const item of selectedProducts) {
                for (const acc of getItemAccessories(item)) {
                    const qty = Math.max(0, Number(acc.quantity || 0));
                    if (!acc.product_id || qty <= 0) continue;
                    const key = accessorySerialKey(acc.accessory_id);
                    const picks = accessorySerialPicks[key] || [];
                    if (picks.length !== qty || picks.some((p) => !p?.inventory_product_id)) {
                        toast.error(
                            `Please select serial number for each unit of accessory "${acc.product_name || "Accessory"}".`,
                        );
                        return;
                    }
                    for (const pick of picks) {
                        const id = pick!.inventory_product_id;
                        if (usedInventoryIds.has(id)) {
                            toast.error(
                                `Duplicate serial "${pick!.serial_no}" selected. Each serial can only be used once.`,
                            );
                            return;
                        }
                        usedInventoryIds.add(id);
                    }
                }
            }

            const productDetailsWithSerials = selectedProducts.map((item) => ({
                ...item,
                accessories: getItemAccessories(item).map((acc: any) => {
                    const key = accessorySerialKey(acc.accessory_id);
                    const picks = (accessorySerialPicks[key] || []).filter(
                        Boolean,
                    ) as AccessorySerialPick[];
                    return {
                        ...acc,
                        issued_inventory_product_ids: picks.map(
                            (p) => p.inventory_product_id,
                        ),
                        serial_numbers: picks.map((p) => p.serial_no),
                    };
                }),
            }));

            setIsSubmitting(true);
            try {
                const transportDetails = buildTransportDetails(values);

                const payload: any = {
                    challan_date: values.challan_date,
                    initiated_by: values.initiated_by,
                    transporter_type: values.transporter_type,
                    transporter_details: transportDetails,
                    warehouse_id: selectedHasAccessories
                        ? (values.warehouse_id || existingWarehouseId || null)
                        : null,
                    note: values.note || null,
                    sales_order_service_id:
                        urlId || (metadata?.sales_order_service_id || metadata?.id),
                    customer_details: customerDetails || {},
                    bill_from: billFrom || {},
                    bill_to: billTo || {},
                    terms_and_conditions: termsAndConditions || null,
                    product_details: productDetailsWithSerials,
                    total_summary: summaryTotals,
                    split_summary: summaryTotals.split_summary,
                    attachments: values.attachments,
                };

                const formData = new FormData();
                if (mode === "edit") {
                    appendToFormData(formData, { ...payload, existingDocuments });
                } else {
                    appendToFormData(formData, payload);
                }

                if (mode === "create") {
                    await dispatch(createChallan(formData)).unwrap();
                } else {
                    const challanId = (initialData as any)?.challan_id;
                    await dispatch(updateChallan({ challanId, payload: formData })).unwrap();
                }

                toast.success(
                    mode === "create"
                        ? "Challan created successfully!"
                        : "Challan updated successfully!"
                );
                if (onSuccess) onSuccess();
                else navigate(-1);
            } catch (error: any) {
                toast.error(error?.message || error?.error || "Failed to save Challan");
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    useEffect(() => {
        dispatch(getWarehouseByUser());
    }, [dispatch]);

    // ── Fetch details on Edit/Create mode ────────────────────────────────────
    useEffect(() => {
        if (mode === "edit" && (initialData as any)?.challan_id) {
            dispatch(fetchChallanById((initialData as any).challan_id));
        } else if (mode === "create" && urlId) {
            // Fetch fresh metadata for new challan instead of relying on state
            dispatch(fetchChallanCreateData(urlId));
        }

        return () => {
             dispatch(clearChallanState());
        };
    }, [dispatch, mode, initialData, urlId]);

    // ── Pre-fill form and sync products when challanDetail is loaded ──────────
    useEffect(() => {
        if (!challanDetail) return;

        if (mode === "edit" && challanDetail.challan) {
            const { challan, items } = challanDetail;
            
            // 1. Sync Products (only those relevant/free + those in this challan)
            setProducts(items || []);
            
            // 2. Sync Selected IDs (items with in_this_challan flag)
            const selectedIds = (items || [])
                .filter((it: any) => it.in_this_challan)
                .map((it: any) => it.sales_order_service_item_id);
            setSelectedProductIds(selectedIds);

            // Seed accessory serial picks from challan snapshot
            const seeded: Record<string, Array<AccessorySerialPick | null>> = {};
            (items || []).forEach((it: any) => {
                if (!it.in_this_challan) return;
                getItemAccessories(it).forEach((acc: any) => {
                    if (!acc?.accessory_id) return;
                    seeded[accessorySerialKey(acc.accessory_id)] =
                        seedPicksFromAccessory(acc);
                });
            });
            setAccessorySerialPicks(seeded);

            const warehouseIdForSerials = challan.warehouse_id || "";
            if (warehouseIdForSerials) {
                (items || []).forEach((it: any) => {
                    if (!it.in_this_challan) return;
                    getItemAccessories(it).forEach((acc: any) => {
                        if (!acc?.accessory_id || !acc?.product_id) return;
                        const key = accessorySerialKey(acc.accessory_id);
                        const includeIds = (seeded[key] || [])
                            .filter(Boolean)
                            .map(
                                (p) =>
                                    (p as AccessorySerialPick)
                                        .inventory_product_id,
                            );
                        ensureAccessorySerialOptions(
                            acc,
                            warehouseIdForSerials,
                            includeIds,
                        );
                    });
                });
            }

            // 3. Populate Formik Values
            const transport = challan.transporter_details || {};
            formik.setValues({
                ...formik.values,
                challan_date: challan.challan_date ? new Date(challan.challan_date).toISOString().split("T")[0] : "",
                initiated_by: challan.initiated_by || "",
                transporter_type: challan.transporter_type || "",
                note: challan.note || "",
                // Transporter fields
                transporter_name: transport.person_name || transport.transporter_name || "",
                person_number: transport.person_number || "",
                docket_number: transport.docket_number || "",
                company_name: transport.company_name || "",
                vehicle_number: transport.truck_number || transport.vehicle_number || "",
                driver_name: transport.driver_name || "",
                driver_number: transport.driver_mobile || transport.driver_number || "",
                carrier_name: transport.carrier_name || "",
                train_number: transport.train_number || "",
                booking_number: transport.booking_number || "",
                origin: transport.origin || "",
                flight_number: transport.flight_number || "",
                awb_number: transport.awb_number || "",
                transport_remarks: transport.remarks || "",
                attachments: null,
                warehouse_id: challan.warehouse_id || "",
            });
            setExistingDocuments(challan.attachments || []);
        } else if (mode === "create" && challanDetail.availableItems) {
            // New logic: fetchChallanCreateData returns { salesOrderService, availableItems }
            const { availableItems } = challanDetail;
            setProducts(availableItems || []);
            setSelectedProductIds([]);
            setAccessorySerialPicks({});
            setAccessorySerialOptions({});
        }
    }, [challanDetail, mode]);

    useScrollToFormikError(formik.errors, formik.submitCount);

    // ── Summary totals (based on checked products inline calculation) ────────
    const summaryTotals = React.useMemo(() => {
        const roundToTwoDecimals = (num: number) => Math.round(num * 100) / 100;

        // Filter valid products based on what's checked
        const selectedProducts = products.filter((p) => {
            const pid = getServiceItemId(p);
            return pid !== null && selectedProductIds.includes(pid);
        });

        // Use fresh data from API
        const currentMetadata = mode === "create" ? challanDetail?.salesOrderService : challanDetail?.challan?.sales_order_service;
        const currencyCode = currentMetadata?.currency_code || "INR";
        const isINR = currencyCode === "INR" || currencyCode === "IND";

        let serviceSubTotal = 0;
        let serviceDiscount = 0;
        let serviceNetTotal = 0;
        let serviceGst = 0;
        let accessorySubTotal = 0;
        let accessoryDiscount = 0;
        let accessoryNetTotal = 0;
        let accessoryGst = 0;

        selectedProducts.forEach((item) => {
            // Quantity calculation
            const qty = Number(item.quantity || item.product_quantity || item.productQuantity) || 1;

            // Gross Total Calculation
            let total = 0;
            if (item.gross_value && Number(item.gross_value) > 0) {
                total = Number(item.gross_value);
            } else {
                let rate = 0;
                if (!isINR && item.rate_in_currency && Number(item.rate_in_currency) > 0) {
                    rate = Number(item.rate_in_currency);
                } else if (item.rate_in && Number(item.rate_in) > 0) {
                    rate = Number(item.rate_in);
                } else if (item.serviceRate && Number(item.serviceRate) > 0) {
                    rate = Number(item.serviceRate);
                } else if (item.service_rate && Number(item.service_rate) > 0) {
                    rate = Number(item.service_rate);
                } else if (item.service_price && Number(item.service_price) > 0) {
                    rate = Number(item.service_price);
                } else {
                    rate = Number(item.unit_price) || 0;
                }
                total = qty * rate;
            }

            // Discount Calculation
            let discountAmount = 0;
            if (item.discount_amount && Number(item.discount_amount) > 0) {
                 discountAmount = Number(item.discount_amount);
            } else {
                 const dType = item.discount_type || "currency";
                 const dValue = Number(item.discount_value || 0);
                 if (dType === "%" || dType === "percentage") {
                     discountAmount = (total * dValue) / 100;
                 } else {
                     discountAmount = Math.min(dValue, total);
                 }
            }

            // Net Taxable Amount
            let netVal = 0;
            if (item.net_taxable_amount && Number(item.net_taxable_amount) > 0) {
                 netVal = Number(item.net_taxable_amount);
            } else if (item.net_value && Number(item.net_value) > 0) {
                 netVal = Number(item.net_value);
            } else {
                 netVal = total - discountAmount;
            }

            // GST Calculation (skip for composition GST customers)
            let gstVal = 0;
            if (isINR && !isCompositionGst) {
                if (item.gst_amount && Number(item.gst_amount) > 0) {
                    gstVal = Number(item.gst_amount);
                } else if (item.gst_value && Number(item.gst_value) > 0) {
                    gstVal = Number(item.gst_value);
                } else if (item.original_gst_value && Number(item.original_gst_value) > 0) {
                    gstVal = Number(item.original_gst_value);
                } else {
                    // Fallback to strict calculation if values were returned null
                    const gstPercent = Number(item.gst_percent || item.gst_percentage || 0);
                    gstVal = (netVal * gstPercent) / 100;
                }
            }

            serviceSubTotal += total;
            serviceDiscount += discountAmount;
            serviceNetTotal += netVal;
            serviceGst += gstVal;

            getItemAccessories(item).forEach((acc: any) => {
                accessorySubTotal += Number(acc.gross_value || 0);
                accessoryDiscount += Number(acc.discount_amount || 0);
                accessoryNetTotal += Number(acc.taxable_amount || acc.net_value || 0);
                if (isINR && !isCompositionGst) {
                    accessoryGst += Number(acc.gst_value || acc.gst_amount || 0);
                }
            });
        });

        const subTotal = serviceSubTotal + accessorySubTotal;
        const discount = serviceDiscount + accessoryDiscount;
        const netTotal = serviceNetTotal + accessoryNetTotal;
        const gst = serviceGst + accessoryGst;

        const roundedServiceSub = Number.isFinite(serviceSubTotal)
            ? roundToTwoDecimals(serviceSubTotal)
            : 0;
        const roundedServiceDis = Number.isFinite(serviceDiscount)
            ? roundToTwoDecimals(serviceDiscount)
            : 0;
        const roundedServiceNet = Number.isFinite(serviceNetTotal)
            ? roundToTwoDecimals(serviceNetTotal)
            : 0;
        const roundedServiceGst =
            isINR && !isCompositionGst && Number.isFinite(serviceGst)
                ? roundToTwoDecimals(serviceGst)
                : 0;

        const roundedAccessorySub = Number.isFinite(accessorySubTotal)
            ? roundToTwoDecimals(accessorySubTotal)
            : 0;
        const roundedAccessoryDis = Number.isFinite(accessoryDiscount)
            ? roundToTwoDecimals(accessoryDiscount)
            : 0;
        const roundedAccessoryNet = Number.isFinite(accessoryNetTotal)
            ? roundToTwoDecimals(accessoryNetTotal)
            : 0;
        const roundedAccessoryGst =
            isINR && !isCompositionGst && Number.isFinite(accessoryGst)
                ? roundToTwoDecimals(accessoryGst)
                : 0;

        const roundedSub = Number.isFinite(subTotal) ? roundToTwoDecimals(subTotal) : 0;
        const roundedDis = Number.isFinite(discount) ? roundToTwoDecimals(discount) : 0;
        const roundedNet = Number.isFinite(netTotal) ? roundToTwoDecimals(netTotal) : 0;
        const roundedGST =
            isINR && !isCompositionGst && Number.isFinite(gst) ? roundToTwoDecimals(gst) : 0;

        // TAX SPLITTING LOGIC BASED ON BILL FROM -> BILL TO STATE
        const normalizeState = (s: any) => (s || "").toString().trim().toLowerCase();
        
        const billFromState = normalizeState(
            billFrom?.org_state || billFrom?.state
        );
        
        const billToState = normalizeState(
            billTo?.state || 
            customerDetails?.billing_address?.state || 
            customerDetails?.state
        );
        
        const isIntraState = !!billFromState && !!billToState && billFromState === billToState;

        let roundedCGST = 0;
        let roundedSGST = 0;
        let roundedIGST = 0;

        if (isINR && !isCompositionGst) {
            if (isIntraState) {
                roundedCGST = roundToTwoDecimals(roundedGST / 2);
                roundedSGST = roundToTwoDecimals(roundedGST / 2);
            } else {
                roundedIGST = roundedGST;
            }
        }

        const grandTotalCalc =
            isINR && !isCompositionGst ? (roundedNet + roundedGST) : roundedNet;
        const finalGrandTotal = (Number.isFinite(grandTotalCalc) && !isNaN(grandTotalCalc) && grandTotalCalc >= roundedNet)
            ? grandTotalCalc 
            : (Number.isFinite(roundedNet) && !isNaN(roundedNet) ? roundedNet : 0);

        const roundedServiceTotal = roundToTwoDecimals(
            isINR && !isCompositionGst ? roundedServiceNet + roundedServiceGst : roundedServiceNet
        );
        const roundedAccessoryTotal = roundToTwoDecimals(
            isINR && !isCompositionGst ? roundedAccessoryNet + roundedAccessoryGst : roundedAccessoryNet
        );

        return {
            subtotal: roundedSub,
            total_product_discount: roundedDis,
            net_taxable_amount: roundedNet,
            total_gst_amount: roundedGST,
            cgst: roundedCGST,
            sgst: roundedSGST,
            igst: roundedIGST,
            isIntraState,
            total: roundToTwoDecimals(finalGrandTotal),
            split_summary: {
                service: {
                    sub_total: roundedServiceSub,
                    discount_value: roundedServiceDis,
                    net_value: roundedServiceNet,
                    gst_value: roundedServiceGst,
                    total_amount: roundedServiceTotal,
                },
                accessories: {
                    sub_total: roundedAccessorySub,
                    discount_value: roundedAccessoryDis,
                    net_value: roundedAccessoryNet,
                    gst_value: roundedAccessoryGst,
                    total_amount: roundedAccessoryTotal,
                },
            },
        };
    }, [products, selectedProductIds, billFrom, billTo, customerDetails, isCompositionGst]);

    // ── Terms & Conditions (from metadata when available) ──────────────
    const groupedTerms = React.useMemo(() => {
        const terms = termsAndConditions || [];
        const grouped: Record<string, string[]> = {};
        terms.forEach((term: any) => {
            const type = term.type || "Other";
            if (!grouped[type]) grouped[type] = [];
            if (Array.isArray(term.options)) grouped[type].push(...term.options);
            else if (typeof term.options === "string") {
                if (term.options.includes("\n"))
                    grouped[type].push(...term.options.split("\n").filter(Boolean));
                else grouped[type].push(term.options);
            } else if (term.option) grouped[type].push(term.option);
        });
        Object.keys(grouped).forEach((t) => {
            grouped[t] = Array.from(new Set(grouped[t]));
        });
        return Object.entries(grouped) as [string, string[]][];
    }, [termsAndConditions]);

    const currencySymbol = metadata?.currency_symbol || metadata?.currency?.symbol || "₹";

    // ── Checkbox helpers ─────────────────────────────────────────────────────
    const selectableProductIds = React.useMemo(
        () => products.map(getServiceItemId).filter((id): id is string => Boolean(id)),
        [products],
    );
    const allChecked =
        selectableProductIds.length > 0 && selectedProductIds.length === selectableProductIds.length;
    const handleSelectAll = (checked: boolean) => {
        setSelectedProductIds(checked ? selectableProductIds : []);
        if (!checked) {
            setAccessorySerialPicks({});
            return;
        }
        setAccessorySerialPicks((prev) => {
            const next = { ...prev };
            products.forEach((p) => {
                const pid = getServiceItemId(p);
                if (!pid || !selectableProductIds.includes(pid)) return;
                getItemAccessories(p).forEach((acc: any) => {
                    if (!acc?.accessory_id) return;
                    const key = accessorySerialKey(acc.accessory_id);
                    if (!next[key] || next[key].length !== Number(acc.quantity || 0)) {
                        next[key] = seedPicksFromAccessory(acc).every((x) => x)
                            ? seedPicksFromAccessory(acc)
                            : buildEmptyPicks(Number(acc.quantity || 0));
                    }
                });
            });
            return next;
        });
    };
    const handleRowCheck = (id: string, checked: boolean) => {
        const product = products.find((p) => getServiceItemId(p) === id);
        if (!product) return;

        if (!checked) {
            const hasLocked = getItemAccessories(product).some(
                (acc: any) => acc?.return_locked || Number(acc?.returned_qty || 0) > 0,
            );
            if (hasLocked || product.has_locked_accessory) {
                toast.warning(
                    "Cannot remove this service — one or more of its accessories are already marked for return. Only those accessories are locked.",
                );
                return;
            }
        }

        setSelectedProductIds((prev) =>
            checked ? [...prev, id] : prev.filter((x) => x !== id),
        );
        if (checked) {
            setAccessorySerialPicks((prev) => {
                const next = { ...prev };
                getItemAccessories(product).forEach((acc: any) => {
                    if (!acc?.accessory_id) return;
                    const key = accessorySerialKey(acc.accessory_id);
                    if (!next[key]) {
                        const seeded = seedPicksFromAccessory(acc);
                        next[key] = seeded.some((x) => x)
                            ? seeded
                            : buildEmptyPicks(Number(acc.quantity || 0));
                    }
                });
                return next;
            });
        } else {
            setAccessorySerialPicks((prev) => {
                const next = { ...prev };
                getItemAccessories(product).forEach((acc: any) => {
                    if (acc?.accessory_id) delete next[accessorySerialKey(acc.accessory_id)];
                });
                return next;
            });
        }
    };

    const selectedHasAccessories = React.useMemo(
        () =>
            products.some((p) => {
                const pid = getServiceItemId(p);
                return pid !== null && selectedProductIds.includes(pid) && getItemAccessories(p).length > 0;
            }),
        [products, selectedProductIds],
    );
    const existingWarehouseId = challanDetail?.challan?.warehouse_id || "";
    const showWarehouse = selectedHasAccessories || Boolean(existingWarehouseId);
    const warehouseReadOnly = mode === "edit" && Boolean(existingWarehouseId);

    // Prefetch accessory serial options when warehouse + selection are ready
    useEffect(() => {
        const warehouseId =
            formik.values.warehouse_id || existingWarehouseId || "";
        if (!warehouseId || !selectedProductIds.length) return;

        products.forEach((p) => {
            const pid = getServiceItemId(p);
            if (!pid || !selectedProductIds.includes(pid)) return;
            getItemAccessories(p).forEach((acc: any) => {
                if (!acc?.accessory_id || !acc?.product_id) return;
                const key = accessorySerialKey(acc.accessory_id);
                const includeIds = (accessorySerialPicks[key] || [])
                    .filter(Boolean)
                    .map(
                        (pick) =>
                            (pick as AccessorySerialPick).inventory_product_id,
                    );
                ensureAccessorySerialOptions(acc, warehouseId, includeIds);
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        formik.values.warehouse_id,
        existingWarehouseId,
        selectedProductIds.join(","),
        products,
    ]);

    const handleCancel = () => {
        if (onCancel) onCancel();
        else navigate(-1);
    };

    // ── Shared th/td styles ──────────────────────────────────────────────────
    const thStyle: React.CSSProperties = {
        padding: "8px",
        borderBottom: "1px solid #eee",
        textAlign: "left",
    };
    const tdStyle: React.CSSProperties = {
        padding: "8px",
        borderBottom: "1px solid #eee",
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            {/* ── Form heading with back arrow ─────────────────────────────────── */}
            <div className="d-flex align-items-center gap-2 mb-3">
                <button
                    type="button"
                    onClick={handleCancel}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        color: MecordColors?.ThemeBGColor,
                        padding: 0,
                    }}
                    title="Cancel / Go Back"
                >
                    <ArrowBackIcon style={{ fontSize: 20 }} />
                </button>
                <h6 className="fw-semibold mb-0" style={{ fontSize: "0.95rem" }}>
                    {mode === "create" ? "Create Challan" : "Edit Challan"}
                </h6>
            </div>
            <hr className="mt-0 mb-3" />

            <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
                {/* ═══════════════════════════════════════════════════════════════
            TOP ROW: Challan Details (left) + Transporter Details (right)
        ════════════════════════════════════════════════════════════════ */}
                <Row className="mb-3">
                    {/* ── Challan Details ───────────────────────────────────────── */}
                    <Col xs={12} md={8}>
                        <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                            Challan Details
                        </h6>
                        <div
                            className="p-3 rounded border mb-3"
                            style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
                        >
                            <Row>
                                {/* Challan Date */}
                                <Col xs={12} md={6}>
                                    <Form.Label className="fw-semibold mt-0" style={commonLabelStyle}>
                                        Challan Date
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="challan_date"
                                        value={formik.values.challan_date}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            if ((e.target.value || "").trim())
                                                setTimeout(
                                                    () => formik.setFieldError("challan_date", undefined),
                                                    0
                                                );
                                        }}
                                        onBlur={formik.handleBlur}
                                        isInvalid={
                                            !!formik.errors.challan_date && formik.touched.challan_date
                                        }
                                        style={commonInputStyle}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.challan_date}
                                    </Form.Control.Feedback>
                                </Col>

                                {/* Initiated By */}
                                <Col xs={12} md={6}>
                                    <Form.Label className="fw-semibold mt-0" style={commonLabelStyle}>
                                        {requiredLabel("Initiated By")}
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="initiated_by"
                                        placeholder="Enter Name"
                                        value={formik.values.initiated_by}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            if ((e.target.value || "").trim())
                                                setTimeout(
                                                    () => formik.setFieldError("initiated_by", undefined),
                                                    0
                                                );
                                        }}
                                        onBlur={formik.handleBlur}
                                        isInvalid={
                                            !!formik.errors.initiated_by &&
                                            formik.touched.initiated_by
                                        }
                                        style={commonInputStyle}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.initiated_by}
                                    </Form.Control.Feedback>
                                </Col>

                                {showWarehouse && (
                                    <Col xs={12} md={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            {warehouseReadOnly ? "Warehouse" : requiredLabel("Warehouse")}
                                        </Form.Label>
                                        <Autocomplete
                                            ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                            options={warehouseByUserList ?? []}
                                            loading={warehouseByUserLoading}
                                            disabled={warehouseReadOnly}
                                            getOptionLabel={(option: any) =>
                                                option.warehouse_name || option.warehouse_code || ""
                                            }
                                            isOptionEqualToValue={(option: any, value: any) =>
                                                option.warehouse_id === value.warehouse_id
                                            }
                                            value={
                                                (warehouseByUserList ?? []).find(
                                                    (opt: any) =>
                                                        opt.warehouse_id === formik.values.warehouse_id,
                                                ) || null
                                            }
                                            onChange={(_event, selected: any) => {
                                                formik.setFieldValue(
                                                    "warehouse_id",
                                                    selected?.warehouse_id || "",
                                                );
                                                // Create only: changing warehouse resets serial picks
                                                if (mode === "create") {
                                                    setAccessorySerialPicks((prev) => {
                                                        const next: Record<
                                                            string,
                                                            Array<AccessorySerialPick | null>
                                                        > = {};
                                                        Object.keys(prev).forEach((key) => {
                                                            next[key] = buildEmptyPicks(
                                                                prev[key]?.length || 0,
                                                            );
                                                        });
                                                        return next;
                                                    });
                                                    setAccessorySerialOptions({});
                                                }
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Select Warehouse"
                                                    variant="outlined"
                                                    size="small"
                                                    error={
                                                        selectedHasAccessories &&
                                                        !formik.values.warehouse_id &&
                                                        formik.submitCount > 0
                                                    }
                                                    helperText={
                                                        selectedHasAccessories &&
                                                        !formik.values.warehouse_id &&
                                                        formik.submitCount > 0
                                                            ? "Warehouse is required for accessories"
                                                            : warehouseReadOnly
                                                              ? "Warehouse cannot be changed after create"
                                                              : ""
                                                    }
                                                    sx={autocompleteDropdownStyles.inputSx}
                                                />
                                            )}
                                        />
                                    </Col>
                                )}

                                {/* SO Number – read-only, pre-filled from metadata */}
                                <Col xs={12} md={12}>
                                    <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                        SO Number
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={soNumber}
                                        readOnly
                                        style={{ ...commonInputStyle, backgroundColor: "#f5f5f5" }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </Col>

                    {/* ── Transporter Details ───────────────────────────────────── */}
                    <Col xs={12} md={4}>
                        <h6 className="fw-semibold mb-2" style={{ fontSize: "0.9rem" }}>
                            Transporter Details
                        </h6>
                        <div
                            className="p-3 rounded border mb-3"
                            style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
                        >
                            {/* Transport Type */}
                            <Row className="mb-3">
                                <Col xs={12}>
                                    <Form.Label
                                        className="fw-semibold mt-0"
                                        style={commonLabelStyle}
                                    >
                                        {requiredLabel("Transporter Type")}
                                    </Form.Label>
                                    <Autocomplete
                                        disablePortal
                                        options={transportTypeOptions}
                                        getOptionLabel={(option: any) =>
                                            typeof option === "string" ? option : option || ""
                                        }
                                        value={formik.values.transporter_type || null}
                                        onChange={(_, selected) => {
                                            const type = selected || "";
                                            formik.setFieldValue("transporter_type", type);
                                            if (type)
                                                setTimeout(
                                                    () =>
                                                        formik.setFieldError(
                                                            "transporter_type",
                                                            undefined
                                                        ),
                                                    0
                                                );
                                            [
                                                "transporter_name",
                                                "person_number",
                                                "docket_number",
                                                "company_name",
                                                "vehicle_number",
                                                "driver_name",
                                                "driver_number",
                                                "carrier_name",
                                                "train_number",
                                                "booking_number",
                                                "origin",
                                                "flight_number",
                                                "awb_number",
                                                "transport_remarks",
                                            ].forEach((f) => formik.setFieldValue(f, ""));
                                        }}
                                        onBlur={() =>
                                            formik.setFieldTouched("transporter_type", true)
                                        }
                                        ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                        renderOption={(props, option) =>
                                            autocompleteDropdownStyles.renderOption(
                                                props,
                                                option,
                                                (opt) => opt
                                            )
                                        }
                                        renderInput={(params) => (
                                            <BootstrapTextField
                                                {...params}
                                                name="transporter_type"
                                                placeholder="Select Transport Type"
                                                error={
                                                    formik.touched.transporter_type &&
                                                    Boolean(formik.errors.transporter_type)
                                                }
                                                helperText={
                                                    formik.touched.transporter_type &&
                                                    formik.errors.transporter_type
                                                }
                                                sx={{ ...autocompleteDropdownStyles.inputSx }}
                                                fullWidth
                                                size="small"
                                            />
                                        )}
                                    />
                                </Col>
                            </Row>

                            {/* Self Pickup */}
                            {formik.values.transporter_type === "Self Pickup" && (
                                <Row className="mb-3">
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Transporter Name
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="transporter_name"
                                            placeholder="Enter Transporter Name"
                                            value={formik.values.transporter_name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Person Number
                                        </Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="person_number"
                                            placeholder="Enter Person Number"
                                            value={formik.values.person_number}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                </Row>
                            )}

                            {/* Courier */}
                            {formik.values.transporter_type === "Courier" && (
                                <Row className="mb-3">
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Company Name
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="company_name"
                                            placeholder="Enter Company Name"
                                            value={formik.values.company_name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Docket Number
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="docket_number"
                                            placeholder="Enter Docket Number"
                                            value={formik.values.docket_number}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                </Row>
                            )}

                            {/* Road */}
                            {formik.values.transporter_type === "Road" && (
                                <Row className="mb-3">
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Transporter Name
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="transporter_name"
                                            placeholder="Enter Transporter Name"
                                            value={formik.values.transporter_name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Vehicle Number
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="vehicle_number"
                                            placeholder="Enter Vehicle Number"
                                            value={formik.values.vehicle_number}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Driver Name
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="driver_name"
                                            placeholder="Enter Driver Name"
                                            value={formik.values.driver_name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Driver Number
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="driver_number"
                                            placeholder="Enter Driver Number"
                                            value={formik.values.driver_number}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                </Row>
                            )}

                            {/* Rail */}
                            {formik.values.transporter_type === "Rail" && (
                                <Row className="mb-3">
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Carrier Name
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="carrier_name"
                                            placeholder="Enter Carrier Name"
                                            value={formik.values.carrier_name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Train Number
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="train_number"
                                            placeholder="Enter Train Number"
                                            value={formik.values.train_number}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Booking Number
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="booking_number"
                                            placeholder="Enter Booking Number"
                                            value={formik.values.booking_number}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Origin
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="origin"
                                            placeholder="Enter Origin"
                                            value={formik.values.origin}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                </Row>
                            )}

                            {/* Air */}
                            {formik.values.transporter_type === "Air" && (
                                <Row className="mb-3">
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Carrier Name
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="carrier_name"
                                            placeholder="Enter Carrier Name"
                                            value={formik.values.carrier_name}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Flight Number
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="flight_number"
                                            placeholder="Enter Flight Number"
                                            value={formik.values.flight_number}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            AWB Number
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="awb_number"
                                            placeholder="Enter AWB Number"
                                            value={formik.values.awb_number}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Origin
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="origin"
                                            placeholder="Enter Origin"
                                            value={formik.values.origin}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                </Row>
                            )}

                            {/* Remarks */}
                            {formik.values.transporter_type && (
                                <Row className="mb-3">
                                    <Col xs={12}>
                                        <Form.Label className="fw-semibold" style={commonLabelStyle}>
                                            Remark
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="transport_remarks"
                                            placeholder="Enter Transport Remark"
                                            value={formik.values.transport_remarks}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            style={commonInputStyle}
                                        />
                                    </Col>
                                </Row>
                            )}
                        </div>
                    </Col>
                </Row>

                <hr />

                {/* ═══════════════════════════════════════════════════════════════
            Customer Details – always shown, no conditions
        ════════════════════════════════════════════════════════════════ */}
                <h5 className="fw-bold mb-3">Customer Details</h5>
                <div
                    className="p-4 rounded-3 mb-4"
                    style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
                >
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <p>
                                <strong>Company Name :</strong>{" "}
                                {customerDetails?.company_name || "-"}
                            </p>
                            <p>
                                <strong>Company Email :</strong>{" "}
                                {customerDetails?.email || "-"}
                            </p>
                            <p>
                                <strong>Customer PO Number :</strong>{" "}
                                {customerDetails?.customer_po_number || "-"}
                            </p>
                            <p>
                                <strong>PO Document :</strong>{" "}
                                {customerDetails?.customer_po_documents &&
                                    Array.isArray(customerDetails.customer_po_documents) &&
                                    customerDetails.customer_po_documents.length > 0 ? (
                                    <div>
                                        {customerDetails.customer_po_documents.map(
                                            (doc: string, index: number) => (
                                                <div key={index} className="mb-1">
                                                    <a
                                                        href={doc}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-decoration-underline"
                                                    >
                                                        View Document {index + 1}
                                                    </a>
                                                </div>
                                            )
                                        )}
                                    </div>
                                ) : customerDetails?.customer_po_document_url ? (
                                    <a
                                        href={customerDetails.customer_po_document_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-decoration-underline"
                                    >
                                        View Document
                                    </a>
                                ) : (
                                    "N/A"
                                )}
                            </p>
                        </div>
                        <div className="col-md-6">
                            <p>
                                <strong>Industry :</strong>{" "}
                                {customerDetails?.industry || "-"}
                            </p>
                            <p>
                                <strong>Company Phone No. :</strong>{" "}
                                {customerDetails?.phone_number || "-"}
                            </p>
                            <p>
                                <strong>PO Date :</strong>{" "}
                                {customerDetails?.customer_po_date ? (
                                    <FormattedDate date={customerDetails.customer_po_date} />
                                ) : (
                                    "-"
                                )}
                            </p>
                        </div>
                    </div>

                    <hr />

                    {/* Additional Contact Details */}
                    <h6 className="fw-bold mb-3">Additional Contact Details</h6>
                    {customerDetails?.contact_details?.length > 0 ? (
                        <div className="row">
                            {customerDetails.contact_details.map(
                                (contact: any, index: number) => (
                                    <div key={index} className="col-md-6 mb-3">
                                        <p>
                                            <strong>Contact Name :</strong> {contact.name || "-"}
                                        </p>
                                        <p>
                                            <strong>Email :</strong> {contact.email || "-"}
                                        </p>
                                        <p>
                                            <strong>Phone Number :</strong>{" "}
                                            {contact.phone_number || "-"}
                                        </p>
                                    </div>
                                )
                            )}
                        </div>
                    ) : (
                        <p className="text-muted mb-0">No additional contacts available.</p>
                    )}
                </div>

                {/* ═══════════════════════════════════════════════════════════════
            Billing Details – always shown, no conditions
        ════════════════════════════════════════════════════════════════ */}
                <h5 className="fw-bold mb-3">Billing Details</h5>
                <div
                    className="p-4 rounded-3 mb-4"
                    style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
                >
                    <div className="row">
                        <div className="col-md-6">
                            <p className="fw-bold mb-1">Bill From :</p>
                            {billFrom ? (
                                <p className="mb-0">
                                    {billFrom.org_address}
                                    <br />
                                    {`${billFrom.org_city}, ${billFrom.org_state}, ${billFrom.org_country} - ${billFrom.org_pincode}`}
                                </p>
                            ) : (
                                <p className="text-muted mb-0">Not available</p>
                            )}
                        </div>
                        <div className="col-md-6">
                            <p className="fw-bold mb-1">Bill To :</p>
                            {billTo ? (
                                <p className="mb-0">
                                    {billTo.address}
                                    <br />
                                    {`${billTo.city}, ${billTo.state}, ${billTo.country} - ${billTo.pin_code}`}
                                </p>
                            ) : (
                                <p className="text-muted mb-0">Not available</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* ═══════════════════════════════════════════════════════════════
            Product Details (with per-row checkboxes)
        ════════════════════════════════════════════════════════════════ */}
                <div>
                    <h5 className="fw-bold mb-3">Product Details</h5>
                    <div
                        className="table-responsive p-3 mb-4"
                        style={{
                            backgroundColor: MecordColors.ActiveBackground,
                            borderRadius: "10px",
                        }}
                    >
                        <div className="overflow-auto">
                            <table
                                style={{
                                    width: "100%",
                                    minWidth: "2000px",
                                    borderCollapse: "collapse",
                                    fontSize: "0.75rem",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                <thead style={{ backgroundColor: MecordColors.LightGrayColor }}>
                                    <tr>
                                        {/* Checkbox – select all */}
                                        <th style={{ ...thStyle, width: 40 }}>
                                            <Form.Check
                                                type="checkbox"
                                                checked={allChecked}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                                title="Select all"
                                            />
                                        </th>
                                        <th style={thStyle}>Sr No.</th>
                                        <th style={thStyle}>Model</th>
                                        <th style={thStyle}>Product</th>
                                        <th style={thStyle}>Serial No.</th>
                                        <th style={thStyle}>Service Category</th>
                                        <th style={thStyle}>Service Method</th>
                                        <th style={thStyle}>Service Type</th>
                                        <th style={thStyle}>Execution Mode</th>
                                        <th style={thStyle}>SAC Code</th>
                                        <th style={thStyle}>Product Quantity</th>
                                        <th style={thStyle}>Service Price</th>
                                        {/* <th style={thStyle}>Rate in Currency</th> */}
                                        <th style={thStyle}>Gross Value</th>
                                        <th style={thStyle}>Discount/Flat</th>
                                        <th style={thStyle}>Discount Value</th>
                                        <th style={thStyle}>Net Value</th>
                                        <th style={thStyle}>GST (%)</th>
                                        <th style={thStyle}>SGST (%)</th>
                                        <th style={thStyle}>CGST (%)</th>
                                        <th style={thStyle}>IGST (%)</th>
                                        {!isCompositionGst && <th style={thStyle}>GST Value</th>}
                                        <th style={thStyle}>Total</th>
                                        {/* <th style={thStyle}>Service Address</th> */}
                                    </tr>
                                </thead>
                                <tbody style={{ backgroundColor: "#fff" }}>
                                    {products.length > 0 ? (
                                        products.map((prod: any, idx: number) => {
                                            const productId = getServiceItemId(prod);
                                            const rowKey = productId || `missing-service-item-${idx}`;
                                            const isChecked = productId !== null && selectedProductIds.includes(productId);

                                            // Serial number - may be a string or array
                                            const serialNo = prod?.serial_number ||
                                                prod?.serial_no ||
                                                (Array.isArray(prod?.serial_numbers)
                                                    ? prod.serial_numbers.join(", ")
                                                    : prod?.serial_numbers) ||
                                                "-";

                                            // Service address
                                            const addr = prod?.service_address_details || prod?.service_address;
                                            const serviceAddress = addr
                                                ? typeof addr === "string"
                                                    ? addr
                                                    : [
                                                        addr.address,
                                                        addr.city,
                                                        addr.state,
                                                        addr.country,
                                                        addr.pincode || addr.pin_code,
                                                        addr.phone ? `Phone: ${addr.phone}` : null,
                                                        addr.phone_number ? `Phone: ${addr.phone_number}` : null,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(", ")
                                                : "-";

                                            // Discount display
                                            const discountType = prod?.discount_type || "currency";
                                            const discountValue = parseFloat(prod?.discount_value || prod?.discount_amount || 0);
                                            const grossValue = parseFloat(prod?.gross_value || prod?.service_price || prod?.item_amount || 0);
                                            const discountCalc =
                                                discountType === "%" || discountType === "percentage"
                                                    ? (grossValue * discountValue) / 100
                                                    : Math.min(discountValue, grossValue);
                                            const accessories = isChecked
                                                ? getItemAccessories(prod)
                                                : [];

                                            return (
                                                <React.Fragment key={rowKey}>
                                                <tr
                                                    style={{
                                                        backgroundColor: isChecked
                                                            ? MecordColors.ActiveBackground
                                                            : undefined,
                                                    }}
                                                >
                                                    {/* Checkbox */}
                                                    <td style={tdStyle}>
                                                        <Form.Check
                                                            type="checkbox"
                                                            checked={isChecked}
                                                            disabled={!productId}
                                                            onChange={(e) => {
                                                                if (!productId) return;
                                                                handleRowCheck(productId, e.target.checked);
                                                            }}
                                                        />
                                                    </td>
                                                    <td style={tdStyle}>{idx + 1}</td>
                                                    {/* Model */}
                                                    <td style={{ ...tdStyle, minWidth: 130 }}>
                                                        {prod?.model_name || prod?.model || "-"}
                                                    </td>
                                                    {/* Product */}
                                                    <td style={{ ...tdStyle, minWidth: 160 }}>
                                                        {prod?.product_name || prod?.name || "-"}
                                                    </td>
                                                    {/* Serial No. */}
                                                    <td style={{ ...tdStyle, minWidth: 140 }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                            <span>{serialNo}</span>
                                                            {serialNo && serialNo !== "-" && (
                                                                <InfoIcon
                                                                    style={{
                                                                        cursor: "pointer",
                                                                        color: MecordColors?.ThemeBGColor || "#007bff",
                                                                        fontSize: "18px",
                                                                    }}
                                                                    onClick={() => fetchProductHistory(serialNo as string)}
                                                                />
                                                            )}
                                                        </div>
                                                    </td>
                                                    {/* Service Category */}
                                                    <td style={{ ...tdStyle, minWidth: 140 }}>
                                                        {prod?.service_category ||
                                                            prod?.service_category_name ||
                                                            "-"}
                                                    </td>
                                                    {/* Service Method */}
                                                    <td style={{ ...tdStyle, minWidth: 120 }}>
                                                        {prod?.service_method ||
                                                            prod?.service_method_name ||
                                                            "-"}
                                                    </td>
                                                    {/* Service Type */}
                                                    <td style={{ ...tdStyle, minWidth: 120 }}>
                                                        {prod?.service_type ||
                                                            prod?.service_type_name ||
                                                            "-"}
                                                    </td>
                                                    {/* Execution Mode */}
                                                    <td style={{ ...tdStyle, minWidth: 120 }}>
                                                        {prod?.execution_mode ||
                                                            prod?.execution_mode_name ||
                                                            "-"}
                                                    </td>
                                                    {/* SAC Code */}
                                                    <td style={{ ...tdStyle, minWidth: 100 }}>
                                                        {prod?.sac_code || prod?.hsn_sac_code || "-"}
                                                    </td>
                                                    {/* Product Quantity */}
                                                    <td style={{ ...tdStyle, minWidth: 120, textAlign: "center" }}>
                                                        {prod?.product_quantity || prod?.quantity || 1}
                                                    </td>
                                                    {/* Service Price */}
                                                    <td style={{ ...tdStyle, minWidth: 120 }}>
                                                        {currencySymbol}{formatIndianAmount(
                                                            prod?.service_price || prod?.unit_price || 0
                                                        )}
                                                    </td>
                                                    {/* Rate in Currency */}
                                                    {/* <td style={{ ...tdStyle, minWidth: 140 }}>
                                                        {prod?.rate_in_currency
                                                            ? `${currencySymbol}${formatIndianAmount(prod.rate_in_currency)}`
                                                            : "-"}
                                                    </td> */}
                                                    {/* Gross Value */}
                                                    <td style={{ ...tdStyle, minWidth: 120 }}>
                                                        {currencySymbol}{formatIndianAmount(grossValue)}
                                                    </td>
                                                    {/* Discount/Flat */}
                                                    <td style={{ ...tdStyle, minWidth: 110 }}>
                                                        {discountType === "%" || discountType === "percentage"
                                                            ? `${formatIndianAmount(discountValue)}%`
                                                            : `${currencySymbol}${formatIndianAmount(discountValue)}`}
                                                    </td>
                                                    {/* Discount Value (computed) */}
                                                    <td style={{ ...tdStyle, minWidth: 120 }}>
                                                        {currencySymbol} {formatIndianAmount(discountCalc)}
                                                    </td>
                                                    {/* Net Value */}
                                                    <td style={{ ...tdStyle, minWidth: 120 }}>
                                                        {currencySymbol}{formatIndianAmount(
                                                            prod?.net_taxable_amount ||
                                                            prod?.net_taxable_value ||
                                                            prod?.net_value ||
                                                            (grossValue - discountCalc)
                                                        )}
                                                    </td>
                                                    {/* GST (%) */}
                                                    <td style={{ ...tdStyle, minWidth: 80, textAlign: "center" }}>
                                                        {prod?.gst_percent ?? prod?.gst_percentage ?? "-"}
                                                        {(prod?.gst_percent || prod?.gst_percentage) ? "%" : ""}
                                                    </td>
                                                    {/* SGST (%) */}
                                                    <td style={{ ...tdStyle, minWidth: 80, textAlign: "center" }}>
                                                        {prod?.sgst_percent ?? prod?.sgst_percentage
                                                            ? `${prod?.sgst_percent ?? prod?.sgst_percentage}%`
                                                            : "-"}
                                                    </td>
                                                    {/* CGST (%) */}
                                                    <td style={{ ...tdStyle, minWidth: 80, textAlign: "center" }}>
                                                        {prod?.cgst_percent ?? prod?.cgst_percentage
                                                            ? `${prod?.cgst_percent ?? prod?.cgst_percentage}%`
                                                            : "-"}
                                                    </td>
                                                    {/* IGST (%) */}
                                                    <td style={{ ...tdStyle, minWidth: 80, textAlign: "center" }}>
                                                        {prod?.igst_percent ?? prod?.igst_percentage
                                                            ? `${prod?.igst_percent ?? prod?.igst_percentage}%`
                                                            : "-"}
                                                    </td>
                                                    {/* GST Value */}
                                                    {!isCompositionGst && (
                                                        <td style={{ ...tdStyle, minWidth: 100 }}>
                                                            {currencySymbol}{formatIndianAmount(
                                                                prod?.gst_amount || prod?.gst_value || 0
                                                            )}
                                                        </td>
                                                    )}
                                                    {/* Total */}
                                                    <td style={{ ...tdStyle, minWidth: 110 }}>
                                                        {currencySymbol}{formatIndianAmount(
                                                            prod?.total || prod?.total_amount || prod?.total_service_amount || 0
                                                        )}
                                                    </td>
                                                    {/* Service Address */}
                                                    {/* <td style={{ ...tdStyle, minWidth: 220, whiteSpace: "normal", maxWidth: 260 }}>
                                                        {serviceAddress}
                                                    </td> */}
                                                </tr>
                                                {accessories.flatMap((acc: any, accIdx: number) => {
                                                    const qty = Math.max(0, Number(acc.quantity || 0));
                                                    const key = accessorySerialKey(acc.accessory_id);
                                                    const picks =
                                                        accessorySerialPicks[key] ||
                                                        buildEmptyPicks(qty);
                                                    const options =
                                                        accessorySerialOptions[key] || [];
                                                    const warehouseId =
                                                        formik.values.warehouse_id ||
                                                        existingWarehouseId ||
                                                        "";
                                                    const isAccessoryLocked =
                                                        Boolean(acc?.return_locked) ||
                                                        Number(acc?.returned_qty || 0) > 0;

                                                    return Array.from({ length: qty || 0 }, (_, unitIdx) => {
                                                        const selectedPick = picks[unitIdx] || null;
                                                        const usedElsewhere = new Set(
                                                            Object.entries(accessorySerialPicks)
                                                                .flatMap(([k, list]) =>
                                                                    list.map((p, i) =>
                                                                        p &&
                                                                        !(
                                                                            k === key &&
                                                                            i === unitIdx
                                                                        )
                                                                            ? p.inventory_product_id
                                                                            : null,
                                                                    ),
                                                                )
                                                                .filter(Boolean) as string[],
                                                        );
                                                        const filteredOptions = options.filter(
                                                            (opt) =>
                                                                !usedElsewhere.has(
                                                                    opt.inventory_product_id,
                                                                ) ||
                                                                opt.inventory_product_id ===
                                                                    selectedPick?.inventory_product_id,
                                                        );
                                                        const isFirstUnit = unitIdx === 0;
                                                        const unitLabel = `${idx + 1}.${accIdx + 1}${
                                                            qty > 1 ? `.${unitIdx + 1}` : ""
                                                        }`;

                                                        return (
                                                    <tr
                                                        key={`${productId}-acc-${acc.accessory_id || accIdx}-u-${unitIdx}`}
                                                        style={{
                                                            backgroundColor: isAccessoryLocked
                                                                ? "#f3f4f6"
                                                                : "#f8fafc",
                                                        }}
                                                    >
                                                        <td style={tdStyle} />
                                                        <td style={tdStyle}>
                                                            {unitLabel}
                                                            {isFirstUnit && isAccessoryLocked && (
                                                                <Tooltip title="Accessory locked — already marked for return">
                                                                    <span
                                                                        className="badge bg-secondary ms-1"
                                                                        style={{ fontSize: "9px" }}
                                                                    >
                                                                        Locked
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </td>
                                                        <td style={{ ...tdStyle, minWidth: 130 }}>
                                                            {acc.model_name || "-"}
                                                        </td>
                                                        <td style={{ ...tdStyle, minWidth: 160 }}>
                                                            <span
                                                                style={{
                                                                    display: "inline-block",
                                                                    marginRight: 6,
                                                                    padding: "1px 6px",
                                                                    borderRadius: 999,
                                                                    backgroundColor: MecordColors.LightThemeBGColor,
                                                                    color: MecordColors.ThemeBGColor,
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                Accessory
                                                            </span>
                                                            {acc.product_name || "-"}
                                                        </td>
                                                        <td style={{ ...tdStyle, minWidth: 180 }}>
                                                            <Autocomplete
                                                                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                                                                size="small"
                                                                options={filteredOptions}
                                                                loading={Boolean(
                                                                    accessorySerialLoading[key],
                                                                )}
                                                                disabled={!warehouseId || isAccessoryLocked}
                                                                getOptionLabel={(option) =>
                                                                    option.serial_no || ""
                                                                }
                                                                isOptionEqualToValue={(a, b) =>
                                                                    a.inventory_product_id ===
                                                                    b.inventory_product_id
                                                                }
                                                                value={selectedPick}
                                                                onOpen={() => {
                                                                    if (isAccessoryLocked) return;
                                                                    if (!warehouseId) {
                                                                        toast.warning(
                                                                            "Select warehouse first",
                                                                        );
                                                                        return;
                                                                    }
                                                                    const includeIds = picks
                                                                        .filter(Boolean)
                                                                        .map(
                                                                            (p) =>
                                                                                (
                                                                                    p as AccessorySerialPick
                                                                                )
                                                                                    .inventory_product_id,
                                                                        );
                                                                    ensureAccessorySerialOptions(
                                                                        acc,
                                                                        warehouseId,
                                                                        includeIds,
                                                                    );
                                                                }}
                                                                onChange={(_e, selected) => {
                                                                    if (isAccessoryLocked) return;
                                                                    setAccessorySerialPicks((prev) => {
                                                                        const current =
                                                                            prev[key] ||
                                                                            buildEmptyPicks(qty);
                                                                        const nextList = [
                                                                            ...current,
                                                                        ];
                                                                        while (
                                                                            nextList.length < qty
                                                                        ) {
                                                                            nextList.push(null);
                                                                        }
                                                                        nextList[unitIdx] =
                                                                            selected
                                                                                ? {
                                                                                      inventory_product_id:
                                                                                          selected.inventory_product_id,
                                                                                      serial_no:
                                                                                          selected.serial_no,
                                                                                      warranty_date:
                                                                                          selected.warranty_date ||
                                                                                          null,
                                                                                  }
                                                                                : null;
                                                                        return {
                                                                            ...prev,
                                                                            [key]: nextList,
                                                                        };
                                                                    });
                                                                }}
                                                                renderInput={(params) => (
                                                                    <TextField
                                                                        {...params}
                                                                        placeholder={
                                                                            isAccessoryLocked
                                                                                ? "Locked"
                                                                                : warehouseId
                                                                                  ? "Select serial"
                                                                                  : "Select warehouse first"
                                                                        }
                                                                        variant="outlined"
                                                                        size="small"
                                                                        error={
                                                                            formik.submitCount >
                                                                                0 &&
                                                                            !selectedPick
                                                                        }
                                                                        sx={
                                                                            autocompleteDropdownStyles.inputSx
                                                                        }
                                                                    />
                                                                )}
                                                            />
                                                            {selectedPick?.warranty_date ? (
                                                                <div
                                                                    className="text-muted"
                                                                    style={{
                                                                        fontSize: 11,
                                                                        marginTop: 4,
                                                                    }}
                                                                >
                                                                    Warranty:{" "}
                                                                    {selectedPick.warranty_date}
                                                                </div>
                                                            ) : null}
                                                        </td>
                                                        <td style={tdStyle}>-</td>
                                                        <td style={tdStyle}>-</td>
                                                        <td style={tdStyle}>-</td>
                                                        <td style={tdStyle}>-</td>
                                                        <td style={tdStyle}>
                                                            {acc.hsn_code || acc.sac_code || "-"}
                                                        </td>
                                                        <td style={{ ...tdStyle, textAlign: "center" }}>
                                                            1
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {isFirstUnit ? (
                                                                <>
                                                                    {currencySymbol}
                                                                    {formatIndianAmount(
                                                                        acc.unit_price || 0,
                                                                    )}
                                                                </>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {isFirstUnit ? (
                                                                <>
                                                                    {currencySymbol}
                                                                    {formatIndianAmount(
                                                                        acc.gross_value || 0,
                                                                    )}
                                                                </>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {isFirstUnit
                                                                ? acc.discount_type ===
                                                                      "percentage" ||
                                                                  acc.discount_type === "%"
                                                                    ? `${formatIndianAmount(acc.discount_value || 0)}%`
                                                                    : `${currencySymbol}${formatIndianAmount(acc.discount_value || 0)}`
                                                                : "-"}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {isFirstUnit ? (
                                                                <>
                                                                    {currencySymbol}
                                                                    {formatIndianAmount(
                                                                        acc.discount_amount || 0,
                                                                    )}
                                                                </>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {isFirstUnit ? (
                                                                <>
                                                                    {currencySymbol}
                                                                    {formatIndianAmount(
                                                                        acc.taxable_amount || 0,
                                                                    )}
                                                                </>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {isFirstUnit
                                                                ? (acc.gst_percentage ?? "-")
                                                                : "-"}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {isFirstUnit
                                                                ? acc.sgst_percentage
                                                                    ? `${acc.sgst_percentage}%`
                                                                    : "-"
                                                                : "-"}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {isFirstUnit
                                                                ? acc.cgst_percentage
                                                                    ? `${acc.cgst_percentage}%`
                                                                    : "-"
                                                                : "-"}
                                                        </td>
                                                        <td style={tdStyle}>
                                                            {isFirstUnit
                                                                ? acc.igst_percentage
                                                                    ? `${acc.igst_percentage}%`
                                                                    : "-"
                                                                : "-"}
                                                        </td>
                                                        {!isCompositionGst && (
                                                            <td style={tdStyle}>
                                                                {isFirstUnit ? (
                                                                    <>
                                                                        {currencySymbol}
                                                                        {formatIndianAmount(
                                                                            acc.gst_value || 0,
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    "-"
                                                                )}
                                                            </td>
                                                        )}
                                                        <td style={tdStyle}>
                                                            {isFirstUnit ? (
                                                                <>
                                                                    {currencySymbol}
                                                                    {formatIndianAmount(
                                                                        acc.total_amount || 0,
                                                                    )}
                                                                </>
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </td>
                                                    </tr>
                                                        );
                                                    });
                                                })}
                                                </React.Fragment>

                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td
                                                colSpan={isCompositionGst ? 24 : 25}
                                                style={{
                                                    ...tdStyle,
                                                    textAlign: "center",
                                                    backgroundColor: "white",
                                                    color: "#666",
                                                    paddingTop: 20,
                                                    paddingBottom: 20,
                                                }}
                                            >
                                                No products added yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Products Modal */}
                    <Modal
                        show={showProductsModal}
                        onHide={() => setShowProductsModal(false)}
                        size="xl"
                        centered
                    >
                        <Modal.Header closeButton>
                            <Modal.Title>Products: {modalProductLabel || "-"}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ overflowX: "auto" }}>
                            <Table className="table align-middle">
                                <thead
                                    style={{ backgroundColor: MecordColors.LightGrayColor }}
                                >
                                    <tr>
                                        <th style={{ minWidth: 80 }}>Sr. No.</th>
                                        <th style={{ minWidth: 150 }}>Product Name</th>
                                        <th style={{ minWidth: 150 }}>Product Code</th>
                                        <th style={{ minWidth: 150 }}>Make</th>
                                        <th style={{ minWidth: 150 }}>Model</th>
                                        <th style={{ minWidth: 150 }}>Serial No.</th>
                                        <th style={{ minWidth: 150, textAlign: "center" }}>
                                            Additional Details
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {modalProducts && modalProducts.length ? (
                                        modalProducts.map((p: any, i: number) => {
                                            const serial =
                                                p.serial_number ??
                                                p.serial_no ??
                                                (p.serials
                                                    ? Array.isArray(p.serials)
                                                        ? p.serials.join(", ")
                                                        : p.serials
                                                    : null) ??
                                                String(i + 1);
                                            return (
                                                <tr key={p.product_id || i}>
                                                    <td>{i + 1}</td>
                                                    <td>{p.product_name || p.name || "-"}</td>
                                                    <td>{p.product_code || p.code || "-"}</td>
                                                    <td>{p.make_name || p.make || "-"}</td>
                                                    <td>{p.model_name || p.model || "-"}</td>
                                                    <td>{serial}</td>
                                                    <td style={{ textAlign: "center" }}>
                                                        <EditIcon
                                                            style={{
                                                                cursor: "pointer",
                                                                color:
                                                                    tableStyles.primaryColor ||
                                                                    MecordColors?.ThemeBGColor,
                                                                fontSize: "20px",
                                                            }}
                                                            onClick={() => {
                                                                setAdditionalDetailsIndex(i);
                                                                setAdditionalDetailsDraft(
                                                                    (p as any).additional_details ?? ""
                                                                );
                                                                setShowAdditionalDetailsModal(true);
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={7} className="text-center text-muted">
                                                No products available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </Modal.Body>
                        <Modal.Footer>
                            <div className="d-flex justify-content-center w-100">
                                <CancelButton
                                    showConfirmation={false}
                                    onClick={() => setShowProductsModal(false)}
                                    disabled={isSubmitting}
                                />
                            </div>
                        </Modal.Footer>
                    </Modal>

                    {/* Additional Details Modal */}
                    <Modal
                        show={showAdditionalDetailsModal}
                        onHide={() => {
                            setShowAdditionalDetailsModal(false);
                            setAdditionalDetailsIndex(null);
                            setAdditionalDetailsDraft("");
                        }}
                        centered
                        className="modal-lg"
                    >
                        <Modal.Header
                            closeButton
                            style={{
                                fontFamily: tableStyles.header.fontFamily,
                                fontSize: tableStyles.header.fontSize,
                                borderBottom: "1px solid #dee2e6",
                            }}
                        >
                            <Modal.Title
                                style={{
                                    fontFamily: tableStyles.header.fontFamily,
                                    fontSize: tableStyles.header.fontSize,
                                    fontWeight: tableStyles.header.fontWeight,
                                }}
                            >
                                Additional Details
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body
                            style={{
                                fontFamily: filterPopoverStyles.fontFamily,
                                fontSize: filterPopoverStyles.fontSize,
                            }}
                        >
                            <Form.Group className="mb-0">
                                <TextEditor
                                    value={additionalDetailsDraft}
                                    onChange={setAdditionalDetailsDraft}
                                    placeholder="Enter additional details..."
                                    minHeightPx={120}
                                    maxHeightPx={200}
                                    blockImages={true}
                                />
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer className="d-flex justify-content-center w-100">
                            <Button
                                variant="secondary"
                                style={{
                                    backgroundColor: "#ffffff",
                                    borderColor: MecordColors?.ThemeBGColor,
                                    color: MecordColors?.ThemeBGColor,
                                    borderRadius: "4px",
                                    padding: "8px 16px",
                                    fontSize: "14px",
                                    fontWeight: "500",
                                    border: `1px solid ${MecordColors?.ThemeBGColor ?? "#AB2123"}`,
                                }}
                                onClick={() => {
                                    setShowAdditionalDetailsModal(false);
                                    setAdditionalDetailsIndex(null);
                                    setAdditionalDetailsDraft("");
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                style={{
                                    backgroundColor: MecordColors?.ThemeBGColor,
                                    borderColor: MecordColors?.ThemeBGColor,
                                    color: MecordColors?.PrimaryButtonTextColor ?? "#fff",
                                }}
                                onClick={() => {
                                    if (modalProductIndex !== null) {
                                        const updated = [...products];
                                        const cur = updated[modalProductIndex] as any;
                                        if (
                                            additionalDetailsIndex !== null &&
                                            Array.isArray(cur.products)
                                        ) {
                                            cur.products = cur.products.map((pp: any, i: number) =>
                                                i === additionalDetailsIndex
                                                    ? { ...pp, additional_details: additionalDetailsDraft }
                                                    : pp
                                            );
                                        }
                                        setProducts(updated);
                                    }
                                    if (additionalDetailsIndex !== null) {
                                        const updatedModal = [...modalProducts];
                                        (updatedModal[
                                            additionalDetailsIndex
                                        ] as any).additional_details = additionalDetailsDraft;
                                        setModalProducts(updatedModal);
                                    }
                                    setShowAdditionalDetailsModal(false);
                                    setAdditionalDetailsIndex(null);
                                    setAdditionalDetailsDraft("");
                                }}
                            >
                                Update
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </div>

                {/* ═══════════════════════════════════════════════════════════════
            Terms & Conditions | Total Summary
        ════════════════════════════════════════════════════════════════ */}
                <Row>
                    <Col md={6}>
                        {groupedTerms.length > 0 && (
                            <div className="mb-4">
                                <h5 className="mb-3 fw-bold">Terms &amp; Conditions</h5>
                                <div
                                    className="border rounded-3 overflow-hidden bg-white"
                                    style={{ borderColor: "#e9ebec" }}
                                >
                                    {groupedTerms.map(
                                        ([type, options], index, arr) => (
                                            <React.Fragment key={index}>
                                                <div className="p-2">
                                                    <h6 className="fw-bold fs-14 mb-2">{type}</h6>
                                                    <ul
                                                        className="text-muted fs-13 ps-3 mb-0"
                                                        style={{ listStyleType: "disc" }}
                                                    >
                                                        {options.map((opt, idx) => (
                                                            <li key={idx} className="mb-1">
                                                                {opt}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                {index !== arr.length - 1 && (
                                                    <hr
                                                        className="my-0 mx-2"
                                                        style={{
                                                            borderTop: "1px solid #e9ebec",
                                                            opacity: 1,
                                                        }}
                                                    />
                                                )}
                                            </React.Fragment>
                                        )
                                    )}
                                </div>
                            </div>
                        )}
                    </Col>
                    <Col md={6}>
                        <ChallanSplitSummary
                            currencySymbol={currencySymbol}
                            splitSummary={summaryTotals.split_summary}
                            grandTotal={summaryTotals.total}
                            isCompositionGst={isCompositionGst}
                        />
                    </Col>
                </Row>

                {/* Accordion styles */}
                <style>
                    {`
            .accordion-challan-note .accordion-button,
            .accordion-challan-files .accordion-button {
              background-color: ${MecordColors?.LightGrayColor} !important;
              color: ${MecordColors?.NormalText} !important;
            }
            .accordion-challan-note .accordion-button:not(.collapsed),
            .accordion-challan-files .accordion-button:not(.collapsed) {
              background-color: ${MecordColors?.LightGrayColor} !important;
              color: ${MecordColors?.NormalText} !important;
            }
          `}
                </style>

                {/* ═══════════════════════════════════════════════════════════════
            Notes | Attach Files
        ════════════════════════════════════════════════════════════════ */}
                <Row className="mb-4 mt-3">
                    <Col md={6}>
                        <Accordion
                            defaultActiveKey="0"
                            className="mb-3 accordion-challan-note"
                        >
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>Notes</Accordion.Header>
                                <Accordion.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            name="note"
                                            placeholder="Enter note"
                                            value={formik.values.note || ""}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        />
                                    </Form.Group>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </Col>
                    <Col md={6}>
                        <Accordion
                            defaultActiveKey="0"
                            className="mb-3 accordion-challan-files"
                        >
                            <Accordion.Item eventKey="0">
                                <Accordion.Header>Attach Files</Accordion.Header>
                                <Accordion.Body>
                                    <Form.Group className="mb-3">
                                        <FileUpload
                                            multiple
                                            value={formik.values.attachments}
                                            existingFiles={existingDocuments}
                                            onChange={(files) => formik.setFieldValue("attachments", files)}
                                            onRemoveExisting={(index) => {
                                                const updatedDocs = [...existingDocuments];
                                                updatedDocs.splice(index, 1);
                                                setExistingDocuments(updatedDocs);
                                            }}
                                        />
                                    </Form.Group>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                    </Col>
                </Row>

                {/* ── Buttons ──────────────────────────────────────────────────── */}
                <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
                    <CancelButton onClick={handleCancel} disabled={isSubmitting} />
                    <PrimarySubmitButton
                        label={mode === "create" ? "Submit" : "Update"}
                        isLoading={isSubmitting}
                        loadingLabel={mode === "create" ? "Submitting..." : "Updating..."}
                    />
                </div>
            </Form>

            {/* Product History Modal */}
            <ProductHistoryModal
                show={showHistoryModal}
                onHide={() => {
                    setShowHistoryModal(false);
                    setProductHistory(null);
                    setSelectedSerialNo("");
                }}
                serialNo={selectedSerialNo}
                productHistory={productHistory}
                loading={loadingHistory}
            />
        </>
    );
};

export default ChallanForm;
